import React from 'react'

import { HStack } from '~/components/elements/HStack'
import { Typography } from '~/components/elements/Typography'
import { useLogEvent } from '~/hooks/useLogEvent'
import { type SortItem, type SortValue } from '~/utils'

import { SortSwitcher, type SortSwitcherProps } from './SortSwitcher'

export type SortDialogItemProps<ColumnKey extends string> = {
  item: SortItem<ColumnKey>
  onChange?: (sortInfo: SortValue<ColumnKey> | null) => void
  value: SortValue<ColumnKey> | null
}

export const SortDialogItem = <ColumnKey extends string>({
  item,
  onChange,
  value: valueProp,
}: SortDialogItemProps<ColumnKey>) => {
  const logEvent = useLogEvent()

  const value = React.useMemo<SortSwitcherProps['value']>(
    () =>
      item.columnKey === valueProp?.columnKey
        ? valueProp?.ascending
          ? 'asc'
          : 'desc'
        : null,
    [item.columnKey, valueProp?.ascending, valueProp?.columnKey],
  )

  const handleChange = React.useCallback<EH<SortSwitcherProps, 'onChange'>>(
    (value) => {
      const ascending = value === 'asc'

      onChange?.(
        valueProp?.columnKey === item.columnKey &&
          valueProp?.ascending === ascending
          ? null
          : { ascending, columnKey: item.columnKey },
      )
      logEvent('Change order', { withAnalysisName: true, withSuffix: true })
    },
    [
      item.columnKey,
      logEvent,
      onChange,
      valueProp?.ascending,
      valueProp?.columnKey,
    ],
  )

  return (
    <HStack
      key={item.columnKey}
      align="center"
      justify="space-between"
      spacing={1}
    >
      <Typography fontWeight={400} variant="heading3">
        {item.label}
      </Typography>

      <SortSwitcher value={value} onChange={handleChange} />
    </HStack>
  )
}
